// Modal to create an investor together with its first SUPERADMIN (one request,
// atomic on the backend). After success it switches to a confirmation view.
// Keyed by the parent so each open starts from a fresh state.
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CreateInvestorPayload, InvestorCreated } from '../types/investor';
import {
  slugify,
  validateCreateInvestor,
  validateSlug,
  type CreateInvestorFormErrors,
  type CreateInvestorFormValues,
} from '../utils/investorForm';

interface CreateInvestorModalProps {
  /** Slugs already in use — drives the live "slug taken" feedback. */
  existingSlugs: ReadonlySet<string>;
  /** Set by the parent once the backend confirms creation. */
  created: InvestorCreated | null;
  submitting: boolean;
  /** Resolves with errors to show in the modal when the backend rejects the request. */
  onSubmit: (payload: CreateInvestorPayload) => Promise<CreateInvestorSubmitErrors | void>;
  onClose: () => void;
}

/**
 * Backend rejection shown inside the modal (a toast would render beneath it).
 * `fields` are i18n keys per input; `form` is an already-translated generic message.
 */
export interface CreateInvestorSubmitErrors {
  fields?: CreateInvestorFormErrors;
  form?: string;
}

const EMPTY_VALUES: CreateInvestorFormValues = {
  name: '',
  slug: '',
  adminName: '',
  adminLastname: '',
  adminEmail: '',
  adminPassword: '',
};

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  type?: string;
  help?: string;
}

// Label/input/error row matching the admin modals' grid layout.
const Field: React.FC<FieldProps> = ({
  id,
  label,
  value,
  onChange,
  error,
  type = 'text',
  help,
}) => (
  <div className="form-control mb-4">
    <div className="grid grid-cols-[auto_1fr] items-center gap-3">
      <label htmlFor={id} className="label-text">
        {label}
      </label>
      <input
        id={id}
        type={type}
        className={`input input-bordered w-full ${error ? 'input-error' : ''}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
      />
      {error && (
        <span id={`${id}-error`} className="text-error text-xs mt-1 col-start-2" role="alert">
          {error}
        </span>
      )}
      {help && !error && (
        <span className="text-base-content/60 text-xs mt-1 col-start-2">{help}</span>
      )}
    </div>
  </div>
);

const CreateInvestorModal: React.FC<CreateInvestorModalProps> = ({
  existingSlugs,
  created,
  submitting,
  onSubmit,
  onClose,
}) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<CreateInvestorFormValues>(EMPTY_VALUES);
  // Slug follows the name until the admin edits it by hand.
  const [slugEdited, setSlugEdited] = useState(false);
  const [errors, setErrors] = useState<CreateInvestorFormErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  // Editing a field clears its error (incl. a backend one like "email already exists").
  const setField = (field: keyof CreateInvestorFormValues, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handleNameChange = (name: string) => {
    setValues((prev) => ({ ...prev, name, slug: slugEdited ? prev.slug : slugify(name) }));
    setErrors((prev) => ({ ...prev, name: undefined, ...(slugEdited ? {} : { slug: undefined }) }));
  };

  const handleSlugChange = (slug: string) => {
    setSlugEdited(true);
    setField('slug', slug);
  };

  // Live slug feedback — computed on render, shown once there is something to check.
  const liveSlugError = values.slug ? validateSlug(values.slug, existingSlugs) : undefined;
  const slugError = liveSlugError ?? errors.slug;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validateCreateInvestor(values, existingSlugs);
    setErrors(newErrors);
    setFormError(null);
    if (Object.keys(newErrors).length) return;

    const rejection = await onSubmit({
      name: values.name.trim(),
      slug: values.slug,
      superadmin: {
        name: values.adminName.trim(),
        lastname: values.adminLastname.trim(),
        email: values.adminEmail.trim(),
        password: values.adminPassword,
      },
    });
    if (rejection?.fields) setErrors(rejection.fields);
    if (rejection?.form) setFormError(rejection.form);
  };

  return (
    <div className="modal modal-open" role="dialog" aria-labelledby="create-investor-title">
      <div className="modal-box">
        <h3 id="create-investor-title" className="font-bold text-lg mb-4">
          {created ? t('admin:investors.created.title') : t('admin:investors.createTitle')}
        </h3>

        {created ? (
          <div className="flex flex-col gap-3">
            <p>{t('admin:investors.created.body', { name: created.investor.name })}</p>
            <ul className="text-sm bg-base-200 rounded-lg p-4 flex flex-col gap-1">
              <li>
                <span className="font-semibold">{t('admin:investors.table.slug')}:</span>{' '}
                <code className="font-mono">{created.investor.slug}</code>
              </li>
              <li>
                <span className="font-semibold">{t('admin:investors.created.superadmin')}:</span>{' '}
                {created.superadmin.email}
              </li>
            </ul>
            <p className="text-sm text-base-content/70">{t('admin:investors.created.hint')}</p>
            <div className="modal-action">
              <button type="button" className="btn btn-primary" onClick={onClose}>
                {t('admin:investors.close')}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            {formError && (
              <div className="alert alert-error mb-4 text-sm" role="alert">
                {formError}
              </div>
            )}
            <h4 className="font-semibold mb-2">{t('admin:investors.sections.investor')}</h4>
            <Field
              id="investor-name"
              label={t('admin:investors.fields.name')}
              value={values.name}
              onChange={handleNameChange}
              error={errors.name && t(errors.name)}
            />
            <Field
              id="investor-slug"
              label={t('admin:investors.fields.slug')}
              value={values.slug}
              onChange={handleSlugChange}
              error={slugError && t(slugError)}
              help={t('admin:investors.fields.slugHelp')}
            />

            <h4 className="font-semibold mb-2 mt-2">{t('admin:investors.sections.superadmin')}</h4>
            <Field
              id="superadmin-name"
              label={t('admin:users.name')}
              value={values.adminName}
              onChange={(v) => setField('adminName', v)}
              error={errors.adminName && t(errors.adminName)}
            />
            <Field
              id="superadmin-lastname"
              label={t('admin:users.lastname')}
              value={values.adminLastname}
              onChange={(v) => setField('adminLastname', v)}
              error={errors.adminLastname && t(errors.adminLastname)}
            />
            <Field
              id="superadmin-email"
              type="email"
              label={t('admin:users.email')}
              value={values.adminEmail}
              onChange={(v) => setField('adminEmail', v)}
              error={errors.adminEmail && t(errors.adminEmail)}
            />
            <Field
              id="superadmin-password"
              type="password"
              label={t('admin:users.password')}
              value={values.adminPassword}
              onChange={(v) => setField('adminPassword', v)}
              error={errors.adminPassword && t(errors.adminPassword)}
            />

            <div className="modal-action">
              <button type="button" className="btn" onClick={onClose} disabled={submitting}>
                {t('admin:users.cancel')}
              </button>
              <button
                type="submit"
                className="btn btn-primary ml-2"
                disabled={submitting || !!liveSlugError}
              >
                {submitting && <span className="loading loading-spinner loading-sm" />}
                {t('admin:investors.create')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateInvestorModal;
